/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Dec 26 2024
 **/

import { OBJECT_TYPE } from "../types/enum";
import { getScaledRectangle } from "./utils";

type ObjectProps = {
	object: IWhiteboardDrawingObject;
	prevObject: IWhiteboardDrawingObject;
};

type SizeProps = {
	screenSize: Resolution;
	imgSrcSize: Resolution;
} & AttrProps;

export type AttrProps = {
	emojiSize: Resolution;
	arrowSize: number;
	imageSrc: CanvasImageSource | undefined;
};

// 현재까지 그려진 오브젝트를 전부 병합했을때 외곽 사각형의 위치 및 크기 정보 반환
export function getCurrentImageSize(objectProps: ObjectProps, sizeProps: SizeProps) {
	const { object, prevObject } = objectProps;

	if (object.points) {
		const position = getOutboundPosition(objectProps, sizeProps);
		const correctionDiff = object.thickness + sizeProps.arrowSize;
		position.left -= correctionDiff;
		position.top -= correctionDiff;
		position.right += correctionDiff;
		position.bottom += correctionDiff;
		if (prevObject && prevObject.position) {
			const prevRect = prevObject.position;
			return {
				left: position.left < prevRect.left ? position.left : prevRect.left,
				top: position.top < prevRect.top ? position.top : prevRect.top,
				right: position.right > prevRect.right ? position.right : prevRect.right,
				bottom: position.bottom > prevRect.bottom ? position.bottom : prevRect.bottom
			};
		}
		return position;
	} else {
		return {
			left: 0,
			top: 0,
			right: 1920,
			bottom: 1080
		};
	}
}

//  object의 bounds 반환
export function getCurrentImageRect(objectProps: ObjectProps, sizeProps: SizeProps) {
	const { object } = objectProps;
	if (object.points) {
		const position = getOutboundPosition(objectProps, sizeProps);
		const correctionDiff = object.thickness + sizeProps.arrowSize;
		position.left -= correctionDiff;
		position.top -= correctionDiff;
		position.right += correctionDiff;
		position.bottom += correctionDiff;
		return {
			...position,
			width: Math.abs(position.right - position.left),
			height: Math.abs(position.bottom - position.top)
		};
	}
}

// 타입별 outbound position 반환
function getOutboundPosition(objectProps: ObjectProps, sizeProps: SizeProps) {
	const { object } = objectProps;

	if (object.points) {
		if (object.objectType === OBJECT_TYPE.CIRCLE) return calcCircleOutbound(object.points);
		else if (object.objectType === OBJECT_TYPE.EMOJI) return calcEmojibound(object.points, sizeProps.emojiSize);
		else if (object.objectType === OBJECT_TYPE.IMAGE) return calcImagebound(object.points, sizeProps.imgSrcSize);
		else if (object.objectType === OBJECT_TYPE.TEXT) return calcTextbound(object.points);
		else return calcOutbound(object.points, sizeProps.screenSize);
	}
	return { left: 0, top: 0, right: sizeProps.screenSize.width, bottom: sizeProps.screenSize.height };
}

// 그리기 진행한 좌표 전체의 외곽 사각형을 반환
function calcOutbound(points: Vector2[], resolution: Resolution) {
	const position = {
		left: resolution.width * 10,
		top: resolution.height * 10,
		right: 0,
		bottom: 0
	};
	points.forEach((point) => {
		if (point.x < position.left) {
			position.left = point.x;
		}
		if (point.y < position.top) {
			position.top = point.y;
		}
		if (point.x > position.right) {
			position.right = point.x;
		}
		if (point.y > position.bottom) {
			position.bottom = point.y;
		}
	});
	return {
		left: position.left,
		top: position.top,
		right: position.right,
		bottom: position.bottom
	};
}

// circle bounds
function calcCircleOutbound(points: Vector2[]) {
	const left = Math.min(points[0].x, points[1].x);
	const top = Math.min(points[0].y, points[1].y);
	const right = Math.max(points[0].x, points[1].x);
	const bottom = Math.max(points[0].y, points[1].y);
	const halfWidth = (right - left) / 2;
	const halfHeight = (bottom - top) / 2;
	const radius = Math.max(halfWidth, halfHeight);
	return {
		left: left - (radius - halfWidth),
		top: top - (radius - halfHeight),
		right: right + (radius - halfWidth),
		bottom: bottom + (radius - halfHeight)
	};
}

// emoji bounds
function calcEmojibound(points: Vector2[], emojiSize: Resolution) {
	const { x, y } = points[1];

	return {
		left: x - emojiSize.width / 2,
		top: y - emojiSize.height / 2,
		right: x + emojiSize.width / 2,
		bottom: y + emojiSize.height / 2
	};
}
// image bounds
function calcImagebound(points: Vector2[], imageSrcSize: Resolution) {
	const { x, y, width, height } = getScaledRectangle(points[0], points[1], imageSrcSize);
	return {
		left: x,
		top: y,
		right: x + width,
		bottom: y + height
	};
}

// text bounds
function calcTextbound(points: Vector2[]) {
	// current bounds
	const bounds = {
		left: points[0].x,
		top: points[0].y,
		right: points[1].x,
		bottom: points[1].y
	};

	// minimum bounds
	const minWidth = 160;
	const minHeight = 50;
	const width = Math.abs(bounds.right - bounds.left);
	const height = Math.abs(bounds.bottom - bounds.top);
	if (width < minWidth || height < minHeight) {
		bounds.right = points[0].x + minWidth;
		bounds.bottom = points[0].y + minHeight;
	}

	return bounds;
}
